import { z } from 'zod';
import { reputationGeneratedMessageSchema, reputationImageStyleSchema, } from './reputation-generation.js';
import { textToBusinessSegmentResponseBody } from './business-segments.js';
import { googleAdsCustomerIdSchema, googleAdsMicrosSchema, negativeKeywordActivityEligibilityStateSchema, negativeKeywordApplyResultSchema, negativeKeywordCadenceStageSchema, negativeKeywordInputSummarySchema, negativeKeywordRemovalResultSchema, negativeKeywordRunTriggerSchema, negativeKeywordSkipReasonSchema, } from './google-ads-negative-keywords.js';
export const IngestJobStatusSchema = z.enum(['success', 'partial_success', 'failure', 'processing']);
// Define a schema for the Google Ads API upload stats
export const googleAdsApiUploadStatsSchema = z.object({
    jobId: z.number().optional(),
    totalAttempted: z.number().optional(),
    totalFailed: z.number().optional(),
    successRate: z.number().optional(),
    failuresByReason: z.record(z.string(), z.number()).optional(),
    retryErrors: z.array(z.any()).optional(), // Google Ads API validation errors from retry attempts
    requestFailures: z.array(z.any()).optional(), // HTTP request failures during upload/retry
});
/**
 * Loop context that travels on the wire as the reserved `__loop` payload field
 * of job-loop iteration events. Stripped from the message before the user
 * handler runs. Dates are ISO strings because the payload is JSON-serialized.
 * The loop's parent job id is not carried here — it's the iteration job row's
 * own `parentJobId`, read from the row rather than duplicated on the wire.
 */
export const jobLoopContextSchema = z.object({
    /** 1-based; transient retries of the same iteration share a runNumber. */
    runNumber: z.number().int().min(1),
    firstRunAt: z.string().datetime(),
    previousRunAt: z.string().datetime().optional(),
    /**
     * Iteration carry-forward state. Stored loosely here (the wire/DB boundary);
     * the per-loop shape is validated at runtime against the loop's `stateSchema`
     * and typed via `z.infer` at the `withJobLoop`/`startJobLoop` call sites.
     */
    state: z.unknown().optional(),
});
export var JobLoopOutcomeEnum;
(function (JobLoopOutcomeEnum) {
    JobLoopOutcomeEnum["Completed"] = "completed";
    JobLoopOutcomeEnum["Failed"] = "failed";
    JobLoopOutcomeEnum["TimedOut"] = "timed_out";
    JobLoopOutcomeEnum["Cancelled"] = "cancelled";
})(JobLoopOutcomeEnum || (JobLoopOutcomeEnum = {}));
export const jobLoopOutcomeSchema = z.nativeEnum(JobLoopOutcomeEnum);
/** The one directive a loop handler returns to the engine: schedule the next iteration. */
export var JobLoopActionEnum;
(function (JobLoopActionEnum) {
    JobLoopActionEnum["Continue"] = "continue";
})(JobLoopActionEnum || (JobLoopActionEnum = {}));
export var BroadcastPauseReason;
(function (BroadcastPauseReason) {
    BroadcastPauseReason["BOUNCE_THRESHOLD_BREACHED"] = "bounce_threshold_breached";
    BroadcastPauseReason["FEEDBACK_STALLED"] = "feedback_stalled";
    BroadcastPauseReason["DEADLINE_REACHED"] = "deadline_reached";
})(BroadcastPauseReason || (BroadcastPauseReason = {}));
export var BounceMetric;
(function (BounceMetric) {
    BounceMetric["HARD"] = "hard";
    BounceMetric["SOFT"] = "soft";
    BounceMetric["TOTAL"] = "total";
})(BounceMetric || (BounceMetric = {}));
export const bounceMetricReadingSchema = z.object({
    metric: z.nativeEnum(BounceMetric),
    rate: z.number(),
    threshold: z.number(),
    tripped: z.boolean(),
});
/**
 * Job metadata owned by the automation domain. Merged flat into
 * `jobsMetadataSchema` — the persisted shape is unchanged, so readers that
 * reach into the JSONB by key (`metadata->>'businessAutomationId'`) still work.
 * Declare new automation fields here rather than in the shared object.
 */
export const automationJobMetadataSchema = z.object({
    businessAutomationId: z.string().uuid().optional(),
    broadcastAlreadyPaused: z
        .object({
        pausedAt: z.string().datetime(),
        pendingRecipientCount: z.number().int(),
    })
        .optional(),
    broadcastPause: z
        .object({
        reason: z.nativeEnum(BroadcastPauseReason),
        resolvedCount: z.number().int(),
        pendingRecipientCount: z.number().int(),
        metrics: z.array(bounceMetricReadingSchema).optional(),
    })
        .optional(),
    broadcastResume: z
        .object({
        resumedBy: z.string(),
        bounceProtectionOverridden: z.boolean(),
    })
        .optional(),
});
/**
 * Reputation copy-generation run state. The parent job carries the plan — which
 * templates and messages the run intends to rewrite — and each child job carries
 * one template's outcome.
 */
export const reputationCopyGenerationMetadataSchema = z.object({
    templateId: z.string().optional(),
    templateName: z.string().optional(),
    displayName: z.string().optional(),
    includeImages: z.boolean().optional(),
    imageStyle: reputationImageStyleSchema.optional(),
    /** Per-template plan, on the parent job only. */
    plan: z
        .array(z.object({
        templateId: z.string(),
        templateName: z.string(),
        displayName: z.string(),
        messages: z.array(reputationGeneratedMessageSchema),
    }))
        .optional(),
    /** Per-message outcome, on a child job only. */
    messages: z.array(reputationGeneratedMessageSchema).optional(),
    /** Why this template kept all of its shipped copy. */
    error: z.string().optional(),
});
export const jobsMetadataSchema = z.object({
    ...automationJobMetadataSchema.shape,
    attempts: z.number().optional(),
    errors: z
        .array(z.object({
        message: z.string().optional(),
        stack: z.string().optional(),
    }))
        .optional(),
    // Observability: the Honeycomb trace this job's handler executed under,
    // captured by withJobTracking at start. `traceService` is the OTEL service
    // name (= Honeycomb dataset) the trace lives in, needed to build a permalink.
    traceId: z.string().optional(),
    traceService: z.string().optional(),
    // Admin backfill loop parent context, persisted at kickoff so the runs list can
    // show which backfill ran, what it was invoked with, and its Honeycomb query.
    backfillName: z.string().optional(),
    honeycombUrl: z.string().optional(),
    // Voice regression run loop parent context, persisted at kickoff so the loop
    // job links back to the voice_test_runs row it drives.
    voiceTestRunId: z.string().optional(),
    businessId: z.string().optional(),
    platformId: z.string().optional(),
    platformIdCount: z.number().optional(),
    businessIdCount: z.number().optional(),
    params: z.record(z.string(), z.unknown()).optional(),
    // Optional resume/progress cursor for cursor-based jobs. For admin backfills,
    // this records the cursor supplied at kickoff; loop state remains authoritative.
    cursor: z.string().nullable().optional(),
    // REPUTATION_COPY_GENERATION_*: the run's plan on the parent job and each
    // template's outcome on its child, read together by the poll endpoint. Jobs have
    // no per-message column, so message state is metadata-derived.
    reputationCopyGeneration: reputationCopyGenerationMetadataSchema.optional(),
    // PARTNER_SEGMENT_GENERATION: the generated segment (or its failure reason),
    // written by the processor handler on completion and read by the poll endpoint.
    segmentGenerationResult: textToBusinessSegmentResponseBody.optional(),
    // Backfill-specific run-result counts returned by a backfill's toJobMetadata
    // (e.g. tools applied, names dropped).
    toolsApplied: z.number().optional(),
    toolsWouldApply: z.number().optional(),
    toolNamesDropped: z.number().optional(),
    // archived-tool-names-cleanup backfill: agent / voice-agent rows whose tool_names had an
    // archived name stripped (or would be stripped when dryRun — read alongside the dryRun flag).
    agentRowsStripped: z.number().optional(),
    voiceAgentRowsStripped: z.number().optional(),
    // Full per-bucket count detail for backfills whose results don't fit the
    // standard record counts (e.g. voice-call-reanalysis skip/delete buckets).
    backfillCounts: z.record(z.string(), z.number()).optional(),
    // JSON ingest job specific fields
    status: IngestJobStatusSchema.optional(),
    /** The tenant has no source file to read yet, rather than the ingest having failed */
    sourceSnapshotMissing: z.boolean().optional(),
    totalRecords: z.number().optional(),
    processedRecords: z.number().optional(),
    successfulRecords: z.number().optional(),
    pendingRecords: z.number().optional(),
    failedRecords: z.number().optional(),
    // Records deliberately not acted on (e.g. already absent, reference conflicts).
    // Distinct from failedRecords, which counts records that errored during processing.
    skippedRecords: z.number().optional(),
    errorSummary: z.record(z.string(), z.number()).optional(),
    error: z.string().optional(),
    successRate: z.number().optional(),
    failuresByExternalId: z
        .record(z.string(), z.object({
        errors: z.array(z.string()),
        categories: z.array(z.string()),
    }))
        .optional(),
    uniqueFailedIds: z.number().optional(),
    totalErrorCount: z.number().optional(),
    // New fields for Google Ads Offline Conversion
    s3Key: z.string().optional(),
    // Google Ads Offline Conversion - keyed by conversion type label
    purchaseS3Key: z.string().optional(),
    leadS3Key: z.string().optional(),
    callS3Key: z.string().optional(),
    purchaseUploadStats: googleAdsApiUploadStatsSchema.optional(),
    leadUploadStats: googleAdsApiUploadStatsSchema.optional(),
    callUploadStats: googleAdsApiUploadStatsSchema.optional(),
    // Example for more detailed per-business dispatch info - can be added if needed
    platformDispatchDetails: z
        .array(z.object({
        businessId: z.string(),
        googleTriggered: z.boolean(),
        metaTriggered: z.boolean(),
    }))
        .optional(),
    dryRun: z.boolean().optional(),
    triggeredSubJobIds: z.array(z.string()).optional(),
    ingestedPhoneRecordCount: z.number().optional(),
    totalUploads: z.number().optional(),
    successfulUploads: z.number().optional(),
    failedUploads: z.number().optional(),
    schemaDefinitionId: z.string().optional(),
    /** Per-schema row counts removed during cascade delete (target + dependent / conflict schemas). */
    cascadeDeletedBySchema: z
        .array(z.object({
        schemaDefinitionId: z.string(),
        schemaName: z.string(),
        deletedCount: z.number(),
    }))
        .optional(),
    /** Per-(schema,field) row counts where a cascaded target's external ID was surgically removed
     * from a JSONB array field. The referencing rows are not deleted — only the matching elements
     * are stripped from the array. Populated when cascade encounters array-of-ref fields. */
    arrayRefRemovals: z
        .array(z.object({
        schemaDefinitionId: z.string(),
        schemaName: z.string(),
        field: z.string(),
        rowsModified: z.number(),
    }))
        .optional(),
    syncedTemplates: z.number().optional(),
    /** Automation template sync: which template this job is syncing (enables polling by templateId). */
    templateId: z.string().uuid().optional(),
    // Google Ads negative keyword V1 run/apply metadata.
    googleCustomerId: googleAdsCustomerIdSchema.optional(),
    adAccountId: z.string().uuid().optional(),
    dateWindowStart: z.string().date().optional(),
    dateWindowEnd: z.string().date().optional(),
    trigger: negativeKeywordRunTriggerSchema.optional(),
    cadenceStage: negativeKeywordCadenceStageSchema.optional(),
    promptVersion: z.string().trim().min(1).max(256).optional(),
    model: z.string().trim().min(1).max(256).optional(),
    activityEligibilityState: negativeKeywordActivityEligibilityStateSchema.optional(),
    skipReason: negativeKeywordSkipReasonSchema.optional(),
    last7dCostMicros: googleAdsMicrosSchema.optional(),
    lastSearchActivityAt: z.string().datetime({ offset: true }).optional(),
    suggestionsCreated: z.number().int().nonnegative().optional(),
    suggestionsSuppressed: z.number().int().nonnegative().optional(),
    suggestionIds: z.array(z.string().uuid()).max(1_000).optional(),
    targetListResourceName: z.string().trim().min(1).max(1_024).optional(),
    targetListName: z.string().trim().min(1).max(512).optional(),
    applyResults: z.array(negativeKeywordApplyResultSchema).max(250).optional(),
    removalResults: z.array(negativeKeywordRemovalResultSchema).max(250).optional(),
    inputSummary: negativeKeywordInputSummarySchema.optional(),
    malformedAiOutputCount: z.number().int().nonnegative().optional(),
    protectedTermBlockedCount: z.number().int().nonnegative().optional(),
    duplicateCoverageBlockedCount: z.number().int().nonnegative().optional(),
    // Firecrawl job result - can contain GeneratedConfig or CrawlPage[]
    firecrawlResult: z.any().optional(),
    // If true, the failure is permanent and should NOT be retried.
    // Default behavior for failures is to retry (throw to put back in queue).
    // Set this to true for permanent errors like invalid configuration, missing accounts, etc.
    isTerminalFailure: z.boolean().optional(),
    /**
     * Write-once loop guards, set on the loop parent job at kickoff and never
     * mutated. Kept separate from the mutable `loop` bookkeeping below so a
     * bookkeeping merge can never clobber the limits the loop runs against.
     */
    loopGuards: z
        .object({
        maxRuns: z.number().int().optional(),
        maxWallTimeMs: z.number().optional(),
        firstRunAt: z.string().datetime().optional(),
    })
        .optional(),
    /**
     * Mutable job-loop bookkeeping. On a loop parent job: lifecycle outcome.
     * On an iteration (child) job: run audit info. `action`/`delayMs` are
     * transient directives a loop handler returns to the engine ("schedule the
     * next iteration"); the engine strips them before persisting.
     */
    loop: z
        .object({
        parentJobId: z.string().uuid().optional(),
        runNumber: z.number().int().optional(),
        action: z.nativeEnum(JobLoopActionEnum).optional(),
        delayMs: z.number().optional(),
        nextRunAt: z.string().datetime().optional(),
        totalRuns: z.number().int().optional(),
        outcome: jobLoopOutcomeSchema.optional(),
        /**
         * Iteration carry-forward state — both the transient handler→engine
         * directive (output of this iteration) and the persisted audit value on
         * the iteration job row. Stored loosely; validated per-loop via stateSchema.
         */
        state: z.unknown().optional(),
    })
        .optional(),
});
export var JobStatusEnum;
(function (JobStatusEnum) {
    JobStatusEnum["PENDING"] = "PENDING";
    JobStatusEnum["IN_PROGRESS"] = "IN_PROGRESS";
    JobStatusEnum["COMPLETED"] = "COMPLETED";
    /** Job's own execution is done but it has spawned child jobs that are still running. */
    JobStatusEnum["AWAITING_CHILDREN"] = "AWAITING_CHILDREN";
    JobStatusEnum["CANCELLED"] = "CANCELLED";
})(JobStatusEnum || (JobStatusEnum = {}));
export var EventTypeNameEnum;
(function (EventTypeNameEnum) {
    EventTypeNameEnum["PARTNER_RESOURCE_DB_INGEST"] = "PARTNER_RESOURCE_DB_INGEST";
    EventTypeNameEnum["PARTNER_RESOURCE_CSV_INGEST"] = "PARTNER_RESOURCE_CSV_INGEST";
    /**
     * Cron-triggered event that fans out PARTNER_RESOURCE_DB_INGEST jobs for every
     * business that has a SQLite partner_database configured with active schema definitions.
     * @see scheduled-partner-db-ingest.handler.ts
     */
    EventTypeNameEnum["SCHEDULED_PARTNER_DB_INGEST_RUN"] = "SCHEDULED_PARTNER_DB_INGEST_RUN";
    EventTypeNameEnum["PARTNER_RESOURCE_JSON_INGEST"] = "PARTNER_RESOURCE_JSON_INGEST";
    EventTypeNameEnum["PARTNER_RESOURCE_BATCH_DELETE"] = "PARTNER_RESOURCE_BATCH_DELETE";
    EventTypeNameEnum["PARTNER_RESOURCE_BATCH_PATCH"] = "PARTNER_RESOURCE_BATCH_PATCH";
    EventTypeNameEnum["GOOGLE_ADS_PHONE_DATA_INGEST"] = "GOOGLE_ADS_PHONE_DATA_INGEST";
    EventTypeNameEnum["GOOGLE_ADS_PHONE_DATA_INGEST_ALL_BUSINESSES"] = "GOOGLE_ADS_PHONE_DATA_INGEST_ALL_BUSINESSES";
    EventTypeNameEnum["GENERIC_PHONE_DATA_INGEST"] = "GENERIC_PHONE_DATA_INGEST";
    EventTypeNameEnum["META_LEADGEN_RECEIVED"] = "META_LEADGEN_RECEIVED";
    EventTypeNameEnum["META_LEADGEN_BACKFILL_REQUESTED"] = "META_LEADGEN_BACKFILL_REQUESTED";
    /**
     * The following are related to processor-metrics
     * @see /apps/services/processor-metrics/src/QueueListener.ts
     */
    EventTypeNameEnum["AGGREGATE_TRACKING_METRICS_RUN"] = "AGGREGATE_TRACKING_METRICS_RUN";
    EventTypeNameEnum["AGGREGATE_TRACKING_METRICS"] = "AGGREGATE_TRACKING_METRICS";
    EventTypeNameEnum["AGGREGATE_MESSAGING_METRICS"] = "AGGREGATE_MESSAGING_METRICS";
    EventTypeNameEnum["BUILD_PARTNER_USER_METADATA_HISTORICAL"] = "BUILD_PARTNER_USER_METADATA_HISTORICAL";
    EventTypeNameEnum["GOOGLE_ADS_OFFLINE_CONVERSION_UPLOAD"] = "GOOGLE_ADS_OFFLINE_CONVERSION_UPLOAD";
    EventTypeNameEnum["META_ADS_OFFLINE_CONVERSION_UPLOAD"] = "META_ADS_OFFLINE_CONVERSION_UPLOAD";
    EventTypeNameEnum["GOOGLE_ADS_METRICS"] = "GOOGLE_ADS_METRICS";
    EventTypeNameEnum["META_ADS_METRICS"] = "META_ADS_METRICS";
    EventTypeNameEnum["UPLOAD_OFFLINE_CONVERSIONS_RUN"] = "UPLOAD_OFFLINE_CONVERSIONS_RUN";
    EventTypeNameEnum["GOOGLE_BUSINESS_PROFILE_REVIEWS_SYNC"] = "GOOGLE_BUSINESS_PROFILE_REVIEWS_SYNC";
    EventTypeNameEnum["GOOGLE_BUSINESS_PROFILE_REVIEWS_SYNC_RUN"] = "GOOGLE_BUSINESS_PROFILE_REVIEWS_SYNC_RUN";
    /**
     * The following are related to processor-exports
     * @see /apps/services/processor-exports/src/QueueListener.ts
     */
    EventTypeNameEnum["SCHEDULED_EXPORTS_RUN"] = "SCHEDULED_EXPORTS_RUN";
    /**
     * The following are realted to processor-automations
     * @see /apps/services/processor-automations/src/QueueListener.ts
     */
    EventTypeNameEnum["BUSINESS_AUTOMATION_RUN"] = "BUSINESS_AUTOMATION_RUN";
    EventTypeNameEnum["AUTOMATION_EVENT"] = "AUTOMATION_EVENT";
    EventTypeNameEnum["COMMUNICATION_JOB"] = "COMMUNICATION_JOB";
    EventTypeNameEnum["CHECK_SEGMENTS_RUN"] = "CHECK_SEGMENTS_RUN";
    EventTypeNameEnum["CHECK_SEGMENTS_RUN_BUSINESS"] = "CHECK_SEGMENTS_RUN_BUSINESS";
    EventTypeNameEnum["CHECK_SEGMENTS_RUN_SEGMENT"] = "CHECK_SEGMENTS_RUN_SEGMENT";
    EventTypeNameEnum["CHECK_SEGMENTS_RUN_SINGLE_USER"] = "CHECK_SEGMENTS_RUN_SINGLE_USER";
    EventTypeNameEnum["PARTNER_SEGMENT_GENERATION"] = "PARTNER_SEGMENT_GENERATION";
    EventTypeNameEnum["SEGMENT_MEMBERSHIP_REFRESHED"] = "SEGMENT_MEMBERSHIP_REFRESHED";
    EventTypeNameEnum["ACTION_STEP_WAKE_UP"] = "ACTION_STEP_WAKE_UP";
    EventTypeNameEnum["UNDO_DELAY_FOR_AUTOMATION"] = "UNDO_DELAY_FOR_AUTOMATION";
    EventTypeNameEnum["AUTOMATION_TEMPLATE_SYNC"] = "AUTOMATION_TEMPLATE_SYNC";
    EventTypeNameEnum["BROADCAST_EVALUATION"] = "BROADCAST_EVALUATION";
    /**
     * The following are background jobs
     * @see /apps/services/processor-misc/src/QueueListener.ts
     */
    EventTypeNameEnum["BACKGROUND_VERIFY_DNS_RECORDS"] = "BACKGROUND_VERIFY_DNS_RECORDS";
    EventTypeNameEnum["CHANNEL_ACCOUNT_MARKED_UNVERIFIED"] = "CHANNEL_ACCOUNT_MARKED_UNVERIFIED";
    EventTypeNameEnum["CHANNEL_ACCOUNT_VERIFIED"] = "CHANNEL_ACCOUNT_VERIFIED";
    EventTypeNameEnum["BACKGROUND_CLEAN_AUTOMATIONS"] = "BACKGROUND_CLEAN_AUTOMATIONS";
    EventTypeNameEnum["BACKGROUND_CHECK_SMS_REGISTRATION_STATUS"] = "BACKGROUND_CHECK_SMS_REGISTRATION_STATUS";
    EventTypeNameEnum["PENDING_RESOURCE_CLEANUP"] = "PENDING_RESOURCE_CLEANUP";
    EventTypeNameEnum["TENANT_CREATED"] = "TENANT_CREATED";
    EventTypeNameEnum["DATE_BASED_TRIGGER_CRON"] = "DATE_BASED_TRIGGER_CRON";
    EventTypeNameEnum["DATE_BASED_TRIGGER_AUTOMATION"] = "DATE_BASED_TRIGGER_AUTOMATION";
    EventTypeNameEnum["ARCHIVE_OLD_JOBS"] = "ARCHIVE_OLD_JOBS";
    EventTypeNameEnum["THIRTY_MIN_CRON"] = "THIRTY_MIN_CRON";
    EventTypeNameEnum["TWILIO_DELIVERY_STATUS_CHECK"] = "TWILIO_DELIVERY_STATUS_CHECK";
    EventTypeNameEnum["VALIDATE_CHANNEL_INTEGRATION_CONFIG"] = "VALIDATE_CHANNEL_INTEGRATION_CONFIG";
    EventTypeNameEnum["GOOGLE_AUDIENCE_PERIODIC_RESYNC"] = "GOOGLE_AUDIENCE_PERIODIC_RESYNC";
    EventTypeNameEnum["OVERDUE_AUTOMATIONS_CHECK"] = "OVERDUE_AUTOMATIONS_CHECK";
    EventTypeNameEnum["FIRECRAWL_JOB"] = "FIRECRAWL_JOB";
    EventTypeNameEnum["VOICE_CONFIG_SYNC"] = "VOICE_CONFIG_SYNC";
    EventTypeNameEnum["VOICE_CONVERSATION_FLOW_REBUILD"] = "VOICE_CONVERSATION_FLOW_REBUILD";
    EventTypeNameEnum["RETELL_MISSING_CALLS_SYNC"] = "RETELL_MISSING_CALLS_SYNC";
    EventTypeNameEnum["REPROCESS_VOICE_CALL_IDENTIFICATIONS"] = "REPROCESS_VOICE_CALL_IDENTIFICATIONS";
    EventTypeNameEnum["FEATURE_CANCELLATION"] = "FEATURE_CANCELLATION";
    EventTypeNameEnum["CONFIGURE_CHANNEL_SENDER_WEBHOOK"] = "CONFIGURE_CHANNEL_SENDER_WEBHOOK";
    EventTypeNameEnum["PROVISION_DEFAULT_EMAIL_CHANNEL"] = "PROVISION_DEFAULT_EMAIL_CHANNEL";
    EventTypeNameEnum["GENERATE_SMS_REGISTRATION_DEFAULTS"] = "GENERATE_SMS_REGISTRATION_DEFAULTS";
    EventTypeNameEnum["AGENT_OUTCOME_CAPTURE"] = "AGENT_OUTCOME_CAPTURE";
    /**
     * Parent of a reputation copy-generation run: fans out one child per template.
     * @see reputation-copy-generation-run.handler.ts
     */
    EventTypeNameEnum["REPUTATION_COPY_GENERATION_RUN"] = "REPUTATION_COPY_GENERATION_RUN";
    /** One template's messages, generated and written together. */
    EventTypeNameEnum["REPUTATION_TEMPLATE_COPY_GENERATION"] = "REPUTATION_TEMPLATE_COPY_GENERATION";
    EventTypeNameEnum["ADMIN_BACKFILL"] = "ADMIN_BACKFILL";
    /**
     * The following are related to processor-sms
     * @see /apps/services/processor-sms/src/QueueListener.ts
     */
    EventTypeNameEnum["INBOUND_SMS_RECEIVED"] = "INBOUND_SMS_RECEIVED";
    EventTypeNameEnum["OUTBOUND_SMS_QUEUED"] = "OUTBOUND_SMS_QUEUED";
    /**
     * The following are related to processor-agents
     * @see /apps/services/processor-agents/src/QueueListener.ts
     */
    EventTypeNameEnum["AGENT_MESSAGE_QUEUED"] = "AGENT_MESSAGE_QUEUED";
    /**
     * The following are related to processor-tracking
     * @see /apps/services/processor-tracking/src/QueueListener.ts
     * These are notification-style events (no job tracking) so the api-public
     * publisher can stay DB-free.
     */
    EventTypeNameEnum["VISITOR_VISIT_TRACKING"] = "VISITOR_VISIT_TRACKING";
    EventTypeNameEnum["VISITOR_IDENTIFICATION_TRACKING"] = "VISITOR_IDENTIFICATION_TRACKING";
    /**
     * The following are related to processor-voice
     * @see /apps/services/processor-voice/src/QueueListener.ts
     */
    EventTypeNameEnum["VOICE_POST_CALL_PROCESS"] = "VOICE_POST_CALL_PROCESS";
    EventTypeNameEnum["SMS_SIMULATION_RUN"] = "SMS_SIMULATION_RUN";
    EventTypeNameEnum["VOICE_REGRESSION_RUN"] = "VOICE_REGRESSION_RUN";
    /**
     * The following are SES event notifications
     * @see /apps/services/processor-automations/src/QueueListener.ts
     */
    EventTypeNameEnum["SES_BOUNCE"] = "SES_BOUNCE";
    EventTypeNameEnum["SES_COMPLAINT"] = "SES_COMPLAINT";
    EventTypeNameEnum["SES_DELIVERY"] = "SES_DELIVERY";
    EventTypeNameEnum["SES_SUBSCRIPTION"] = "SES_SUBSCRIPTION";
    EventTypeNameEnum["SES_CLICK"] = "SES_CLICK";
    EventTypeNameEnum["SES_OPEN"] = "SES_OPEN";
    EventTypeNameEnum["SES_SEND"] = "SES_SEND";
})(EventTypeNameEnum || (EventTypeNameEnum = {}));
export var VoiceConfigSyncComponentEnum;
(function (VoiceConfigSyncComponentEnum) {
    VoiceConfigSyncComponentEnum["AGENT_SETTINGS"] = "agent_settings";
    VoiceConfigSyncComponentEnum["MCP_TOOLS"] = "mcp_tools";
    VoiceConfigSyncComponentEnum["CALL_TRANSFERS"] = "call_transfers";
})(VoiceConfigSyncComponentEnum || (VoiceConfigSyncComponentEnum = {}));
//# sourceMappingURL=jobs.js.map